// src/components/Header.js
import { useEffect, useState, useRef } from "react";
import { Row, Col, Navbar, Nav, Container, Badge } from "react-bootstrap";
import { Bell, LogOut } from "lucide-react";
import { FaBars } from "react-icons/fa";
import { NavLink, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { useNotification } from "../context/NotificationContext";
import { useSidebar } from "../context/SidebarContext";
import ConfirmDialog from "./ConfirmDialog";
import logo from "../assets/images/logo.png";
import { useLocation } from "react-router-dom";
import { scroller } from "react-scroll";
import "../styles/Header.scss";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const Header = () => {
  const { user, logout } = useAuth();
  const { unreadCount, updateUnreadCount } = useNotification();
  const { setMobileOpen } = useSidebar();
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const location = useLocation();
  const [showModuleMenu, setShowModuleMenu] = useState(false);
  const moduleMenuRef = useRef(null);
  const [navExpanded, setNavExpanded] = useState(false);

  const handleScrollTo = (sectionId) => {
    if (location.pathname === "/landing-page") {
      // Already on landing page, just scroll
      scroller.scrollTo(sectionId, {
        smooth: true,
        offset: -70,
        duration: 75,
      });
    } else {
      // Go to landing page first, pass scroll target in state
      navigate("/landing-page", { state: { scrollTo: sectionId } });
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        moduleMenuRef.current &&
        !moduleMenuRef.current.contains(event.target)
      ) {
        setShowModuleMenu(false);
      }
    };

    if (showModuleMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showModuleMenu]);


  useEffect(() => {
    // Close mobile navbar and module mega on route change
    setNavExpanded(false);
    setShowModuleMenu(false);
  }, [location.pathname]);


  useEffect(() => {
    if (!user || !token || user.role_id === 1 || user.role_id === 6) return;

    const roleBasedId = (() => {
      const role = user?.role_id;
      if (role === 1) return localStorage.getItem("admin_id");
      if (role === 2) return localStorage.getItem("doctor_id");
      if (role === 3) return localStorage.getItem("receptionist_id");
      if (role === 4) return localStorage.getItem("patient_id");
      if (role === 5) return localStorage.getItem("lab_id");
      if (role === 6) return localStorage.getItem("superadmin_id");
      return null;
    })();

    const fetchUnreadCount = async () => {
      try {
        const res = await axios.get(
          `${BASE_URL}/api/notification/getNotifications`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: {
              user_id: roleBasedId,
              user_type:
                user.role_id === 1
                  ? "admin"
                  : user.role_id === 2
                    ? "doctor"
                    : user.role_id === 3
                      ? "receptionist"
                      : user.role_id === 4
                        ? "patient"
                        : user.role_id === 5
                          ? "lab"
                          : user.role_id === 6
                            ? "superadmin"
                            : "",
              hospital_id: user.hospital_id,
              sub_hospital_id: user.sub_hospital_id,
            },
          }
        );
        const unread = res.data.filter((n) => n.is_read === 0).length;
        updateUnreadCount(unread);
      } catch {
        updateUnreadCount(0);
      }
    };

    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [user, token, updateUnreadCount]);

  const moduleTabs = [
    "Administrative",
    "Patient",
    "Lab/Technician",
    "Receptionist",
    "OPD/Daycare",
    "Payroll",
    "Blood Bank",
    "CSSD",
    "IPD & Emergency",
    "TPA",
    "Doctor",
    "LIS/RIS",
    "Pharmacy",
    "MRD",
    "Bio Waste",
    "Purchase & Inventory",
    "CRM",
    "Accounts",
    "Asset",
    "Ambulance",
  ];

  // route mapping (IMPORTANT)
  const moduleRoutes = {
    "Administrative": "/service/administrative",
    "Patient": "/service/patient",
    "Lab/Technician": "/service/lab-technician",
    "Receptionist": "/service/receptionist",
    "OPD/Daycare": "/service/opd-daycare",
    "Payroll": "/service/payroll",
    "Blood Bank": "/service/bloodbank",
    "CSSD": "/service/cssd",
    "IPD & Emergency": "/service/ipd",
    "TPA": "/service/tpa",
    "Doctor": "/service/doctor",
    "LIS/RIS": "/service/lis-ris",
    "Pharmacy": "/service/pharmacy",
    "MRD": "/service/mrd",
    "Bio Waste": "/service/bio-waste",
    "Purchase & Inventory": "/service/purchase-and-inventory",
    "CRM": "/service/crm",
    "Accounts": "/service/accounts",
    "Asset": "/service/asset",
    "Ambulance": "/service/ambulance",
  };

  return (
    <Navbar expand="lg" fixed="top" className="primary-class-header" expanded={navExpanded} onToggle={(isExpanded) => setNavExpanded(isExpanded)}>
      <Container >
        {/* Left side: Hamburger + Logo */}
        <div className="d-flex align-items-center">
          {/* Hamburger for small screens (only for logged-in users to open sidebar) */}
          {user && (
            <button
              className="btn btn-link text-white me-2 d-lg-none d-md-none p-2 "
              onClick={() => setMobileOpen((prev) => !prev)}
              aria-label="Open sidebar"
            >
              <FaBars size={20} />
            </button>
          )}

          <Navbar.Brand
            as="div"
            role="button"
            onClick={() => logout()}
            style={{ cursor: "pointer" }}
          >
            <img
              src={logo}
              alt="HospEase Solution"
              className="logo-img d-inline-block align-top"
            />
          </Navbar.Brand>


        </div>

        {/* Center links - visible only when NOT logged in (desktop) */}
        {!user && (
          <div className="py-3 px-3">
            {/* Hamburger Toggle for small screens */}
            <Navbar.Toggle aria-controls="landing-nav" className="ms-auto" />

            {/* Collapsible Nav (hamburger menu for md and below) */}
            <Navbar.Collapse id="landing-nav">
              <Nav className="ms-auto">
                <Nav.Link as={NavLink} to="/" end className="header-link" onClick={() => setNavExpanded(false)}>
                  Home
                </Nav.Link>

                <Nav.Link as={NavLink} to="/about-us" className="header-link" onClick={() => setNavExpanded(false)}>
                  About
                </Nav.Link>

                <Nav.Link as={NavLink} to="/features" className="header-link" onClick={() => setNavExpanded(false)}>
                  Feature
                </Nav.Link>

                <Nav.Link
                  className="header-link"
                  onClick={(e) => {
                    e.stopPropagation();
                    // On mobile open the sliding panel and show modules inside it
                    if (window.innerWidth < 992) {
                      setNavExpanded(true);
                      setShowModuleMenu(true);
                    } else {
                      setShowModuleMenu((prev) => !prev);
                    }
                  }}
                >
                  Module
                </Nav.Link>

                <Nav.Link as={NavLink} to="/plans" className="header-link" onClick={() => setNavExpanded(false)}>
                  Plan
                </Nav.Link>

                <Nav.Link as={NavLink} to="/blogs1" className="header-link" onClick={() => setNavExpanded(false)}>
                  Blogs
                </Nav.Link>
                <Nav.Link
                  as={Link}
                  to="/login"
                  className="header-link"
                  onClick={() => setNavExpanded(false)}
                >
                  Login
                </Nav.Link>
              </Nav>

            </Navbar.Collapse>
            {!user && showModuleMenu && (
              <div className={`landing-module-mega ${showModuleMenu ? 'open' : ''}`} ref={moduleMenuRef}>
                <Container >
                  <Row>
                    {moduleTabs.map((module, index) => (
                      <Col
                        key={index}
                        lg={3}
                        md={4}
                        sm={12}
                        xs={12}
                        className="mb-2"
                      >
                        <span
                          className="landing-module-item"
                          onClick={() => {
                            setShowModuleMenu(false);
                            setNavExpanded(false);
                            navigate(moduleRoutes[module]);
                          }}
                        >
                          {module}
                        </span>
                      </Col>
                    ))}
                  </Row>
                </Container>
              </div>
            )}

            {/* Overlay to close sliding panel on mobile or module menu */}
            {!user && (navExpanded || showModuleMenu) && (
              <div
                className="mobile-nav-overlay"
                onClick={() => {
                  setNavExpanded(false);
                  setShowModuleMenu(false);
                }}
                aria-hidden="true"
              />
            )}

          </div>
        )}
        {/* Right side: Notification + Logout (always visible when logged in) */}
        {user && (
          <div className="d-flex align-items-center">
            {/* Notifications */}
            {user.role_id !== 1 && user.role_id !== 6 && (
              <Nav.Link
                as={Link}
                to="/notification"
                className="position-relative text-white me-3"
                onClick={() => updateUnreadCount(0)}
              >
                <Bell size={18} color="#fff" />
                {unreadCount > 0 && (
                  <Badge
                    bg="danger"
                    pill
                    className="position-absolute top-0 start-100 translate-middle"
                  >
                    {unreadCount}
                  </Badge>
                )}
              </Nav.Link>
            )}

            {/* Logout */}
            <ConfirmDialog
              trigger={
                <button
                  className="btn btn-link p-0 ms-2"
                  style={{ border: "none", background: "transparent" }}
                >
                  <LogOut size={18} color="#fff" />
                </button>
              }
              title="Logout Confirmation"
              description="Are you sure you want to logout?"
              confirmLabel="Yes"
              cancelLabel="Cancel"
              onConfirm={() => {
                logout();
                navigate("/login");
              }}
            />
          </div>
        )}
      </Container>
    </Navbar>
  );
};

export default Header;
